import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Copy, Mail, Check, Trash2, Package, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending_payment: { label: "Zahlung ausstehend", color: "bg-amber-100 text-amber-800 border-amber-300" },
  paid: { label: "Bezahlt — zu versenden", color: "bg-blue-100 text-blue-800 border-blue-300" },
  issued: { label: "Ausgestellt", color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  sent: { label: "Versandt", color: "bg-teal-100 text-teal-800 border-teal-300" },
  redeemed: { label: "Eingelöst", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelled: { label: "Storniert", color: "bg-gray-100 text-gray-600 border-gray-300" },
};

const STATUS_ORDER = ["pending_payment", "paid", "issued", "sent", "redeemed", "cancelled"];

export default function VouchersTab({ pwd }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const headers = { "X-Admin-Password": pwd };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/vouchers`, { headers });
      setOrders(data);
    } catch (err) {
      toast.error("Bestellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pwd]);
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("voucher-updated", handler);
    window._adminPwd = pwd;
    return () => window.removeEventListener("voucher-updated", handler);
  }, [pwd]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = orders.filter((o) => filter === "all" || o.status === filter);
  const counts = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = orders.filter((o) => o.status === s).length;
    return acc;
  }, {});

  const doAction = async (order, action) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/vouchers/${order.id}/${action}`,
        {},
        { headers }
      );
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      if (selected?.id === order.id) setSelected(data);
      toast.success("Status aktualisiert.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aktion fehlgeschlagen.");
    }
  };

  const doDelete = async (order) => {
    if (!window.confirm(`Bestellung ${order.order_number} wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/vouchers/${order.id}`, { headers });
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
      if (selected?.id === order.id) setSelected(null);
      toast.success("Bestellung gelöscht.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("In Zwischenablage kopiert.");
  };

  return (
    <div data-testid="vouchers-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Gutscheine</p>
          <h2 className="font-serif text-3xl text-[#2C2A26]">Gutschein-Bestellungen</h2>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#757067] hover:text-[#BA6D52]"
          data-testid="vouchers-refresh"
        >
          <RefreshCw size={14} /> Aktualisieren
        </button>
      </div>

      {/* Status-Filter */}
      <div className="flex flex-wrap gap-2 mb-8">
        <FilterPill label={`Alle (${orders.length})`} active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all" />
        {STATUS_ORDER.map((s) =>
          counts[s] > 0 ? (
            <FilterPill
              key={s}
              label={`${STATUS_LABELS[s].label} (${counts[s]})`}
              active={filter === s}
              onClick={() => setFilter(s)}
              testid={`filter-${s}`}
            />
          ) : null
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-[#BA6D52]" size={24} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[#E3DECE] p-16 text-center">
          <Package className="mx-auto text-[#A8A399] mb-4" size={32} />
          <p className="text-sm text-[#757067]">Keine Bestellungen in dieser Ansicht.</p>
        </div>
      ) : (
        <div className="border border-[#E3DECE]">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAF7] text-xs uppercase tracking-[0.2em] text-[#757067]">
              <tr>
                <th className="text-left px-4 py-3">Nr.</th>
                <th className="text-left px-4 py-3">Produkt</th>
                <th className="text-right px-4 py-3">Betrag</th>
                <th className="text-left px-4 py-3">Käufer</th>
                <th className="text-left px-4 py-3">Empfänger</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Datum</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E3DECE]" data-testid="vouchers-list">
              {filtered.map((o) => (
                <tr key={o.id} className="hover:bg-[#FAFAF7]" data-testid={`voucher-row-${o.order_number}`}>
                  <td className="px-4 py-3 font-mono text-xs text-[#BA6D52] font-semibold">{o.order_number}</td>
                  <td className="px-4 py-3">{o.product_name}</td>
                  <td className="px-4 py-3 text-right font-serif text-base">
                    {Number(o.amount_eur).toFixed(2)} €
                    {o.remaining_value_eur !== undefined && o.remaining_value_eur < o.amount_eur && (
                      <div className="text-xs font-sans text-[#D68A64]">
                        Rest {Number(o.remaining_value_eur).toFixed(2)} €
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#5C574E]">
                    {o.buyer_first_name} {o.buyer_last_name}
                    <div className="text-xs text-[#8A7A68]">{o.buyer_email}</div>
                  </td>
                  <td className="px-4 py-3 text-[#5C574E]">
                    {o.recipient_name || <span className="text-[#A8A399] italic">—</span>}
                    {o.occasion && <div className="text-xs text-[#8A7A68]">{o.occasion}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-xs border ${STATUS_LABELS[o.status]?.color || "bg-gray-100"}`}>
                      {STATUS_LABELS[o.status]?.label || o.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#8A7A68]">
                    {new Date(o.created_at).toLocaleDateString("de-DE")}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelected(o)}
                      className="text-[#BA6D52] text-xs uppercase tracking-wider hover:underline"
                      data-testid={`voucher-detail-${o.order_number}`}
                    >
                      Öffnen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <VoucherDetailModal
          order={selected}
          onClose={() => setSelected(null)}
          onAction={doAction}
          onDelete={doDelete}
          onCopy={copy}
        />
      )}
    </div>
  );
}

function FilterPill({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] border transition-colors ${
        active
          ? "bg-[#BA6D52] text-white border-[#BA6D52]"
          : "bg-transparent text-[#757067] border-[#E3DECE] hover:border-[#BA6D52]"
      }`}
    >
      {label}
    </button>
  );
}

function VoucherDetailModal({ order, onClose, onAction, onDelete, onCopy }) {
  const [redeemAmount, setRedeemAmount] = useState("");
  const [redeemInvoice, setRedeemInvoice] = useState("");
  const [redeemNote, setRedeemNote] = useState("");
  const remaining = order.remaining_value_eur !== undefined ? order.remaining_value_eur : order.amount_eur;
  const canRedeem = order.status !== "pending_payment" && order.status !== "cancelled" && remaining > 0;

  const doRedeem = async () => {
    const amount = Number(redeemAmount);
    if (!amount || amount <= 0) {
      toast.error("Bitte einen Betrag > 0 eingeben.");
      return;
    }
    if (amount > remaining + 0.001) {
      toast.error(`Restguthaben beträgt nur ${remaining.toFixed(2)} €.`);
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/admin/vouchers/${order.id}/redeem-partial`,
        { amount_eur: amount, related_invoice_number: redeemInvoice.trim(), note: redeemNote.trim() },
        { headers: { "X-Admin-Password": order._pwd || (window._adminPwd || "") } }
      );
      // Reload via parent: onAction mit dummy action übergeben, damit die Liste aktualisiert wird
      Object.assign(order, data);
      setRedeemAmount("");
      setRedeemInvoice("");
      setRedeemNote("");
      toast.success(`${amount.toFixed(2)} € angerechnet — Rest: ${data.remaining_value_eur.toFixed(2)} €`);
      // Trigger parent refresh
      window.dispatchEvent(new CustomEvent("voucher-updated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anrechnung fehlgeschlagen.");
    }
  };

  const nextActions = {
    pending_payment: [{ label: "Als bezahlt markieren", action: "mark-paid", icon: <Check size={14} />, primary: true }],
    paid: [{ label: "Als ausgestellt markieren", action: "mark-issued", icon: <Check size={14} />, primary: true }],
    issued: [{ label: "Als versandt markieren", action: "mark-sent", icon: <Check size={14} />, primary: true }],
    sent: [{ label: "Vollständig einlösen", action: "mark-redeemed", icon: <Check size={14} />, primary: true }],
    redeemed: [],
    cancelled: [],
  };
  const actions = nextActions[order.status] || [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#E3DECE]"
        onClick={(e) => e.stopPropagation()}
        data-testid="voucher-detail-modal"
      >
        <div className="p-6 border-b border-[#E3DECE]">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#8A7A68] mb-1">Gutschein</div>
              <div className="font-mono text-2xl text-[#BA6D52] font-bold flex items-center gap-3" data-testid="voucher-detail-number">
                {order.order_number}
                <button onClick={() => onCopy(order.order_number)} className="text-[#8A7A68] hover:text-[#BA6D52]">
                  <Copy size={16} />
                </button>
              </div>
              {order.invoice_number && (
                <div className="text-xs text-[#8A7A68] mt-2">
                  LexOffice-Rechnung: <span className="font-mono">{order.invoice_number}</span>
                </div>
              )}
              {order.invoice_error && (
                <div className="text-xs text-red-600 mt-1 italic">
                  ⚠ LexOffice-Fehler: {order.invoice_error}
                </div>
              )}
            </div>
            <button onClick={onClose} className="text-[#8A7A68] hover:text-[#2C2A26] text-2xl leading-none">×</button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <Row label="Produkt" value={order.product_name} />
          <Row label="Guthaben ursprünglich" value={<span className="font-serif">{Number(order.amount_eur).toFixed(2)} €</span>} />
          <Row label="Restguthaben" value={
            <span className={`font-serif text-xl ${remaining > 0 ? "text-[#D68A64]" : "text-[#A8A399]"}`} data-testid="voucher-detail-remaining">
              {Number(remaining).toFixed(2)} €
            </span>
          } />
          <Row label="Status" value={
            <span className={`inline-block px-2 py-0.5 text-xs border ${STATUS_LABELS[order.status]?.color}`}>
              {STATUS_LABELS[order.status]?.label}
            </span>
          } />

          <div className="border-t border-[#E3DECE] pt-4">
            <div className="text-xs uppercase tracking-[0.25em] text-[#8A7A68] mb-3">Käufer</div>
            <div className="text-sm">
              <div className="font-medium">{order.buyer_first_name} {order.buyer_last_name}</div>
              <div>{order.buyer_street}</div>
              <div>{order.buyer_zip} {order.buyer_city}</div>
              <div className="mt-2 flex items-center gap-2">
                <a href={`mailto:${order.buyer_email}`} className="text-[#BA6D52] hover:underline">
                  <Mail size={12} className="inline" /> {order.buyer_email}
                </a>
                {order.buyer_phone && <span className="text-[#8A7A68]">· {order.buyer_phone}</span>}
              </div>
            </div>
          </div>

          {(order.recipient_name || order.occasion || order.dedication) && (
            <div className="border-t border-[#E3DECE] pt-4">
              <div className="text-xs uppercase tracking-[0.25em] text-[#8A7A68] mb-3">Für den Vordruck (handschriftlich übertragen)</div>
              {order.recipient_name && <Row label="Für" value={order.recipient_name} />}
              {order.occasion && <Row label="Anlass" value={order.occasion} />}
              {order.dedication && (
                <div className="mt-3 bg-[#FBF7EC] border border-[#E3DECE] p-3 text-sm italic text-[#5C574E] whitespace-pre-wrap">
                  {order.dedication}
                </div>
              )}
            </div>
          )}

          {canRedeem && (
            <div className="border-t border-[#E3DECE] pt-4">
              <div className="text-xs uppercase tracking-[0.25em] text-[#8A7A68] mb-3">Guthaben anrechnen (bei Menü-Einsatz)</div>
              <div className="bg-[#FBF7EC] border border-[#D68A64] p-4 space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#8A7A68] block mb-1">Betrag (€)</label>
                    <input
                      type="number"
                      min="1"
                      max={remaining}
                      step="0.01"
                      value={redeemAmount}
                      onChange={(e) => setRedeemAmount(e.target.value)}
                      className="w-full border border-[#E3DECE] px-3 py-2 font-serif text-lg"
                      data-testid="voucher-redeem-amount"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#8A7A68] block mb-1">Rechnungs-Nr. (optional)</label>
                    <input
                      type="text"
                      value={redeemInvoice}
                      onChange={(e) => setRedeemInvoice(e.target.value)}
                      placeholder="RE26018"
                      className="w-full border border-[#E3DECE] px-3 py-2 font-mono text-sm"
                      data-testid="voucher-redeem-invoice"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#8A7A68] block mb-1">Notiz (optional)</label>
                  <input
                    type="text"
                    value={redeemNote}
                    onChange={(e) => setRedeemNote(e.target.value)}
                    placeholder="z. B. Familientisch für Familie Müller"
                    className="w-full border border-[#E3DECE] px-3 py-2 text-sm"
                    data-testid="voucher-redeem-note"
                  />
                </div>
                <button
                  onClick={doRedeem}
                  disabled={!redeemAmount}
                  className="inline-flex items-center gap-2 bg-[#D68A64] hover:bg-[#C07B58] disabled:opacity-40 text-white px-4 py-2 text-xs uppercase tracking-[0.15em]"
                  data-testid="voucher-redeem-btn"
                >
                  Betrag anrechnen
                </button>
              </div>
            </div>
          )}

          {order.redemptions?.length > 0 && (
            <div className="border-t border-[#E3DECE] pt-4">
              <div className="text-xs uppercase tracking-[0.25em] text-[#8A7A68] mb-3">Anrechnungs-Historie</div>
              <div className="space-y-2 text-sm">
                {order.redemptions.map((r, i) => (
                  <div key={i} className="flex justify-between border-b border-[#F0EBDC] pb-2 last:border-0">
                    <div>
                      <div className="text-[#5C574E]">{new Date(r.redeemed_at).toLocaleString("de-DE")}</div>
                      {r.note && <div className="text-xs text-[#8A7A68] italic">{r.note}</div>}
                      {r.related_invoice_number && <div className="text-xs font-mono text-[#BA6D52]">{r.related_invoice_number}</div>}
                    </div>
                    <span className="font-serif text-[#2C2A26]">− {Number(r.amount_eur).toFixed(2)} €</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-[#E3DECE] pt-4">
            <div className="text-xs uppercase tracking-[0.25em] text-[#8A7A68] mb-3">Zeitstempel</div>
            <div className="text-xs text-[#5C574E] space-y-1">
              <div>Bestellt: {new Date(order.created_at).toLocaleString("de-DE")}</div>
              {order.paid_at && <div>Bezahlt: {new Date(order.paid_at).toLocaleString("de-DE")}</div>}
              {order.issued_at && <div>Ausgestellt: {new Date(order.issued_at).toLocaleString("de-DE")}</div>}
              {order.sent_at && <div>Versandt: {new Date(order.sent_at).toLocaleString("de-DE")}</div>}
              {order.redeemed_at && <div>Eingelöst: {new Date(order.redeemed_at).toLocaleString("de-DE")}</div>}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-[#E3DECE] bg-[#FAFAF7] flex flex-wrap gap-3 justify-between">
          <div className="flex gap-2 flex-wrap">
            {actions.map((a) => (
              <button
                key={a.action}
                onClick={() => onAction(order, a.action)}
                data-testid={`voucher-action-${a.action}`}
                className="inline-flex items-center gap-2 bg-[#BA6D52] hover:bg-[#A55D48] text-white px-4 py-2 text-xs uppercase tracking-[0.15em]"
              >
                {a.icon} {a.label}
              </button>
            ))}
          </div>
          {(order.status === "pending_payment" || order.status === "cancelled") && (
            <button
              onClick={() => onDelete(order)}
              className="inline-flex items-center gap-2 text-red-600 hover:bg-red-50 border border-red-300 px-4 py-2 text-xs uppercase tracking-[0.15em]"
              data-testid="voucher-delete-btn"
            >
              <Trash2 size={14} /> Löschen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs uppercase tracking-[0.2em] text-[#8A7A68]">{label}</span>
      <span className="text-sm text-[#2C2A26] text-right">{value}</span>
    </div>
  );
}
